/* In-memory providers over the seed data.

   Session-persistent: mutations stick until the tab is reloaded, so the write
   paths can be exercised properly rather than being no-ops. Nothing is written
   anywhere — reload and the seed is back.

   The latency simulation is deliberate. Screens that only ever see instant data
   get built without loading states, and then the first real Graph call exposes
   every one of them at once. */

import { withPromotions } from '../lib/promote';
import type { INotifyOutcome, INotifyRequest, IOnboardOutcome, IOnboardRequest } from './onboarding/OnboardingClient';
import type { ILeaveSource } from '../lib/leaveRecord';
import type { ILeaveOutcome } from './graph/LeaveSyncClient';
import type { IWindDownFacts } from '../lib/windDown';
import {
  LEVEL_GROUPS,
  allRoleGroups,
  grantsFor,
  levelFromGroups,
  planAppOverride,
  planLevelChange,
  planRevokeAll,
  type AppRole,
  type HubLevel,
} from '@hird/access';
import type {
  AssignmentReason,
  DepotId,
  IAssignment,
  INetwork,
  IJoinerCase, IOpenedCase,
  IOpenCase,
  IRoleTemplate,
} from '../models';

/* A depot with no network record yet. Written out rather than left undefined so
   the first save on an unfilled depot produces a whole record instead of one
   with three fields in it. */
const EMPTY_LOCATION: ILocation = {
  short: '',
  addr: '',
  postcode: '',
  phone: '',
  role: '',
  contact: '',
  comms: '',
  access: '',
  hours: '',
  notes: '',
};

const EMPTY_NETWORK: INetwork = {
  provider: '',
  wan: '',
  wanType: '',
  speed: '',
  wanAcct: '',
  wanSupport: '',
  wanEnds: '',
  failover: '',
  block: '',
  dns: '',
  subnets: [],
  ssids: [],
  vpn: [],
};
import type {
  IApplication,
  IArticle,
  IAttachment,
  IAuditEntry,
  IBackup,
  IChecklistRun,
  IChecklistTemplate,
  ICredential,
  ICurrentUser,
  ICustomRecord,
  IDepot,
  IDevice,
  IDomain,
  IGroup,
  ILicenceSku,
  ILocation,
  ILocations,
  IModule,
  INetworks,
  IPerson,
  IRecordType,
  IRecoveryPlan,
  IRestoreTest,
  IRoleDefinition,
  IStatusTile,
  ISupplier,
} from '../models';
import type {
  IAccessService,
  IApplicationService,
  IArticleService,
  IAssignResult,
  IAttachmentService,
  IAuditService,
  IBackupService,
  ICredentialService,
  IDepotService,
  IDeviceService,
  IDirectoryAccess,
  IDirectoryService,
  ITrainingScopeService,
  ITrainingScope,
  TrainingScopeRefusal,
  IDomainService,
  IGroupService,
  ILedgerServices,
  ILicenceService,
  ILocationService,
  INetworkService,
  IOnboardingService,
  ILeaveSyncService,
  IWindDownService,
  IPeopleService,
  IProcedureService,
  IRecordTypeService,
  ISettingsService,
  IStatusService,
  ISupplierService,
  ISyncService,
  IJoinerService,
} from './IServices';
import * as seed from './mockData';

const LATENCY_MS = 140;

function later<T>(value: T): Promise<T> {
  return new Promise((resolve) => setTimeout(() => resolve(value), LATENCY_MS));
}

/** Deep-ish clone so callers mutating a returned array cannot corrupt the store. */
function copy<T>(value: T): T {
  return structuredClone(value);
}

function nextId(prefix: string, existing: { id: string }[]): string {
  const n = existing.reduce((max, item) => {
    const parsed = parseInt(item.id.replace(/^\D+/, ''), 10);
    return Number.isFinite(parsed) && parsed > max ? parsed : max;
  }, 0);
  return `${prefix}${n + 1}`;
}

function stamp(): string {
  const d = new Date();
  const pad = (n: number) => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())} ${pad(d.getHours())}:${pad(d.getMinutes())}`;
}

/* ─────────────────────────── The store ─────────────────────────── */

const store = {
  people: copy(seed.PEOPLE),
  groups: copy(seed.GROUPS),
  devices: copy(seed.DEVICES),
  /* Entra group id -> the graphIds in it.
     Simon at Level 2 and one other person at Level 1, which is the shape the
     real tenant has after the migration: three administrators and everybody
     else on the same rung. */
  membership: {
    [LEVEL_GROUPS[2]]: [seed.PEOPLE[0].graphId as string],
    [LEVEL_GROUPS[1]]: [seed.PEOPLE[1].graphId as string],
  } as Record<string, string[]>,
  assignments: copy(seed.ASSIGNMENTS),
  skus: copy(seed.SKUS),
  networks: copy(seed.NETWORKS),
  locations: copy(seed.LOCATIONS),
  applications: copy(seed.APPLICATIONS),
  suppliers: copy(seed.SUPPLIERS),
  domains: copy(seed.DOMAINS),
  credentials: copy(seed.CREDENTIALS),
  articles: copy(seed.ARTICLES),
  templates: copy(seed.TEMPLATES),
  runs: copy(seed.RUNS),
  audit: copy(seed.AUDIT),
  backups: copy(seed.BACKUPS),
  plans: copy(seed.PLANS),
  tests: copy(seed.TESTS),
  tiles: copy(seed.STATUS_TILES),
  attachments: copy(seed.ATTACHMENTS),
  recordTypes: copy(seed.RECORD_TYPES),
  customRecords: copy(seed.CUSTOM_RECORDS),
  modules: copy(seed.MODULES),
  roles: copy(seed.ROLES),
  /* Starts with an address, so the notifications screen has something to show
     in a demo rather than an empty state that reads as broken. */
  settings: { 'digest.recipients': 'it@example.com' } as Record<string, string>,
};

/* Every write lands here too, because a mock that skips the audit trail lets you
   build screens that forget to write one. */
function audit(entry: Omit<IAuditEntry, 'when'>): void {
  store.audit.unshift({ ...entry, when: stamp() });
}

/* ─────────────────────────── Providers ─────────────────────────── */

class MockPeopleService implements IPeopleService {
  list(): Promise<IPerson[]> {
    return later(copy(store.people));
  }
  get(id: string): Promise<IPerson | null> {
    return later(copy(store.people.find((p) => p.id === id) ?? null));
  }
  async saveNotes(id: string, notes: string): Promise<void> {
    const person = store.people.find((p) => p.id === id);
    if (!person) throw new Error(`No person ${id}`);
    person.notes = notes;
    audit({ who: 'Simon Ford', act: 'Updated notes', obj: person.name, icon: 'ti-note' });
    await later(undefined);
  }
  async update(id: string, patch: Partial<IPerson>): Promise<IPerson> {
    const index = store.people.findIndex((p) => p.id === id);
    if (index < 0) throw new Error(`No person ${id}`);
    store.people[index] = { ...store.people[index], ...copy(patch) };
    audit({ who: 'Simon Ford', act: 'Updated person', obj: store.people[index].name, icon: 'ti-user' });
    return later(copy(store.people[index]));
  }
  async remove(id: string): Promise<void> {
    const person = store.people.find((p) => p.id === id);
    store.people = store.people.filter((p) => p.id !== id);
    if (person) audit({ who: 'Simon Ford', act: 'Removed person', obj: person.name, icon: 'ti-trash' });
    await later(undefined);
  }
  async create(person: Omit<IPerson, 'id'>): Promise<IPerson> {
    const created: IPerson = { ...copy(person), id: nextId('p', store.people) };
    store.people.push(created);
    audit({ who: 'Simon Ford', act: 'Added person', obj: created.name, icon: 'ti-user-plus' });
    return later(copy(created));
  }
}

class MockDeviceService implements IDeviceService {
  list(): Promise<IDevice[]> {
    return later(copy(store.devices));
  }
  get(id: string): Promise<IDevice | null> {
    return later(copy(store.devices.find((d) => d.id === id) ?? null));
  }
  getByTag(tag: string): Promise<IDevice | null> {
    const match = store.devices.find((d) => d.tag.toLowerCase() === tag.toLowerCase());
    return later(copy(match ?? null));
  }
  async create(device: Omit<IDevice, 'id'>): Promise<IDevice> {
    const created: IDevice = { ...copy(device), id: nextId('d', store.devices) };
    store.devices.push(created);
    audit({ who: 'Simon Ford', act: 'Added device', obj: `${created.tag} ${created.model}`, icon: 'ti-plus' });
    return later(copy(created));
  }
  async update(id: string, patch: Partial<IDevice>): Promise<IDevice> {
    const index = store.devices.findIndex((d) => d.id === id);
    if (index < 0) throw new Error(`No device ${id}`);
    const updated = { ...store.devices[index], ...copy(patch) };
    store.devices[index] = updated;
    audit({ who: 'Simon Ford', act: 'Updated device', obj: updated.tag, icon: 'ti-device-laptop' });
    return later(copy(updated));
  }
  async remove(id: string): Promise<void> {
    const device = store.devices.find((d) => d.id === id);
    store.devices = store.devices.filter((d) => d.id !== id);
    if (device) audit({ who: 'Simon Ford', act: 'Removed device', obj: device.tag, icon: 'ti-trash' });
    await later(undefined);
  }

  async assignments(): Promise<IAssignment[]> {
    return later(
      copy(store.assignments).sort((a, b) => (b.from || '').localeCompare(a.from || '')),
    );
  }

  /* The same three steps in the same order as the real one, so a test that
     proves the order here is proving something about the shipped behaviour and
     not about the mock. */
  async assign(
    deviceId: string,
    personId: string | null,
    reason: AssignmentReason,
    when: string = new Date().toISOString().slice(0, 10),
  ): Promise<IAssignResult> {
    const problems: string[] = [];
    let closed: IAssignment | undefined;
    let opened: IAssignment | undefined;

    for (const spell of store.assignments.filter((a) => a.device === deviceId && !a.to)) {
      spell.to = when;
      closed = copy(spell);
    }

    const index = store.devices.findIndex((d) => d.id === deviceId);
    if (index < 0) throw new Error(`No device ${deviceId}`);
    store.devices[index] = { ...store.devices[index], user: personId };

    if (personId) {
      opened = {
        id: nextId('as', store.assignments),
        device: deviceId,
        person: personId,
        from: when,
        reason,
        notes: '',
      };
      store.assignments.push(opened);
    }

    const who = store.people.find((x) => x.id === personId)?.name;
    audit({
      who: 'Simon Ford',
      act: personId ? 'Assigned device' : 'Unassigned device',
      obj: personId
        ? `${store.devices[index].tag} → ${who ?? personId}`
        : store.devices[index].tag,
      icon: 'ti-device-laptop',
    });

    return later({ device: copy(store.devices[index]), closed, opened, problems });
  }
}

/* Group membership, in memory.
 *
 * Deliberately a real implementation rather than a stub that resolves nothing.
 * The access step is the one part of onboarding that writes to Entra, so it is
 * the part most likely to be wrong, and a mock that returns empty would make
 * the screen render as though nobody has any access at all — which is how you
 * demonstrate a bug instead of a feature.
 *
 * The planner is the same one the real repository calls, so what this does to
 * its in-memory sets is what the live one does to the directory.
 */
/* The Training Matrix's ten departments and one scoped administrator, so the
   control can be seen without the real site. The refusals are reachable too:
   anybody not in ROWS below comes back as no-employee-row, which is the
   branch most likely to be met in anger. */
class MockTrainingScopeService implements ITrainingScopeService {
  private static readonly DEPTS = [
    'Drivers', 'Engineers', 'Machine Moving - Office Teams', 'Machine Moving - Site Teams',
    'Machine Moving - Sub Contractors', 'Office', 'Sales', 'Sub Contractors',
    'Training Staff - External', 'Training Staff - Internal',
  ];

  private rows: Record<string, ITrainingScope> = {
    'sf@hird.co.uk': {
      itemId: '1', name: 'Simon Ford',
      departments: ['Office', 'Machine Moving - Sub Contractors'],
    },
  };

  async departments(): Promise<string[]> {
    return MockTrainingScopeService.DEPTS.slice();
  }

  async get(email: string): Promise<ITrainingScope | TrainingScopeRefusal> {
    return this.rows[email.trim().toLowerCase()] ?? 'no-employee-row';
  }

  async set(itemId: string, departments: string[]): Promise<void> {
    if (!departments.length) {
      throw new Error('Pick at least one department, or change their access level instead.');
    }
    const key = Object.keys(this.rows).find((k) => this.rows[k].itemId === itemId);
    if (key) this.rows[key] = { ...this.rows[key], departments: departments.slice() };
  }
}

class MockDirectoryService implements IDirectoryService {
  async get(graphId: string): Promise<IDirectoryAccess> {
    const memberOf = this.held(graphId);
    const level = levelFromGroups(memberOf);
    return later({ level, grants: grantsFor(level, memberOf), memberOf });
  }

  async setLevel(graphId: string, level: HubLevel | null): Promise<IDirectoryAccess> {
    this.apply(graphId, planLevelChange(level, level ? this.held(graphId) : []));
    audit({
      who: 'Simon Ford',
      act: level ? 'Set hub level' : 'Removed hub access',
      obj: `${nameFor(graphId)}${level ? ` — Level ${level}` : ''}`,
      icon: 'ti-shield-lock',
    });
    return this.get(graphId);
  }

  async setAppRole(graphId: string, appId: string, role: AppRole | null): Promise<IDirectoryAccess> {
    this.apply(graphId, planAppOverride(appId, role, role === null ? this.held(graphId) : []));
    audit({
      who: 'Simon Ford',
      act: 'Changed app access',
      obj: `${nameFor(graphId)} — ${appId}: ${role ?? 'follows level'}`,
      icon: 'ti-apps',
    });
    return this.get(graphId);
  }

  async revokeAll(graphId: string): Promise<IDirectoryAccess> {
    this.apply(graphId, planRevokeAll(this.held(graphId)));
    audit({ who: 'Simon Ford', act: 'Revoked all access', obj: nameFor(graphId), icon: 'ti-user-off' });
    return this.get(graphId);
  }

  private held(graphId: string): string[] {
    return allRoleGroups().filter((g) => (store.membership[g] ?? []).indexOf(graphId) >= 0);
  }

  private apply(graphId: string, plan: { leave: string[]; join: string[] }): void {
    for (const g of plan.leave) {
      store.membership[g] = (store.membership[g] ?? []).filter((id) => id !== graphId);
    }
    for (const g of plan.join) {
      const held = store.membership[g] ?? [];
      if (held.indexOf(graphId) < 0) held.push(graphId);
      store.membership[g] = held;
    }
  }
}

function nameFor(graphId: string): string {
  return store.people.find((p) => p.graphId === graphId)?.name ?? graphId;
}

/* The folder and the welcome email, pretended.
 *
 * Deliberately pretends to SUCCEED rather than to be unavailable. The point of
 * the mock is to show the flow working, and a step that always reports "not
 * configured in this build" would make the finished screen look broken to
 * anybody being shown it.
 *
 * It does keep the two outcomes that matter apart, because they lead to
 * different work: a brand new folder sends a welcome email, and a folder that
 * was already there does not. Pressing it twice is safe and the second press
 * says so, which is the behaviour the real Function has and the thing somebody
 * needs to believe before they will press it at all.
 */
/* Canned, but shaped like the real answers, including the awkward ones: a
   department EZ Leave does not list and a manager who has not used it yet are
   what the screen most needs to be seen showing. */
class MockLeaveSyncService implements ILeaveSyncService {
  async onboard(p: ILeaveSource): Promise<ILeaveOutcome> {
    return later({
      kind: 'created',
      message: 'Added to EZ Leave. HR need to add their working pattern and entitlement before they can book leave.',
      problems: p.manager ? [] : ['No manager was set: Blueprint does not say who it is.'],
    });
  }
  async offboard(_p: ILeaveSource, lastDay: string): Promise<ILeaveOutcome> {
    return later({
      kind: 'closed',
      message: `Marked as a leaver in EZ Leave, last day ${lastDay}. Their leaver pay is worked out from there.`,
      problems: [],
    });
  }
}

/* A leaver with one of every kind of group, so the plan screen can be seen showing
   the lot: two it can remove, and the four reasons it cannot. Nothing is changed;
   it only answers, after a pause long enough to see the progress bar move. */
/* later() with a delay of its own, so the wind-down's progress bar can be watched. */
const pause = <T,>(value: T, ms: number): Promise<T> =>
  new Promise((resolve) => setTimeout(() => resolve(value), ms));

class MockWindDownService implements IWindDownService {
  async preflight(): Promise<void> { return pause(undefined, 300); }

  async inspect(): Promise<IWindDownFacts> {
    return pause({
      signInEnabled: true,
      directLicences: ['mock-e3'],
      groupLicenceCount: 1,
      memberships: [
        { id: 'mock-sales', type: '#microsoft.graph.group', name: 'Sales team', securityEnabled: true, groupTypes: [] },
        { id: 'mock-hull', type: '#microsoft.graph.group', name: 'Depot Hull (Team)', groupTypes: ['Unified'], mailEnabled: true },
        { id: 'mock-dyn', type: '#microsoft.graph.group', name: 'All staff', groupTypes: ['DynamicMembership'] },
        { id: 'mock-ad', type: '#microsoft.graph.group', name: 'AD-Synced Finance', onPremisesSyncEnabled: true },
        { id: 'mock-dl', type: '#microsoft.graph.group', name: 'Hull mailing list', mailEnabled: true, groupTypes: [] },
        { id: 'mock-role', type: '#microsoft.graph.directoryRole', name: 'Helpdesk Administrator' },
      ],
    }, 400);
  }

  async blockSignIn(): Promise<'blocked'> { return pause('blocked' as const, 500); }
  async revokeSessions(): Promise<void> { return pause(undefined, 500); }
  async removeFromGroup(): Promise<void> { return pause(undefined, 450); }
  async removeLicences(): Promise<void> { return pause(undefined, 500); }
}

class MockOnboardingService implements IOnboardingService {
  private readonly made = new Set<string>();

  async onboard(req: IOnboardRequest): Promise<IOnboardOutcome> {
    const first = !this.made.has(req.upn);
    this.made.add(req.upn);
    const folder = `${req.displayName ?? req.upn} (${req.depot || (req.upn.split('@')[0] ?? '')})`;
    audit({
      who: 'Simon Ford',
      act: first ? 'Created personal folder' : 'Checked personal folder',
      obj: folder,
      icon: 'ti-folder',
    });
    return later({
      ok: true,
      emailSent: first && !req.folderOnly,
      message: first
        ? `Folder "${folder}" created and the welcome email has been sent.`
        : `Folder "${folder}" was already there, so no email was sent — they have been set up before.`,
    });
  }

  async notify(req: INotifyRequest): Promise<INotifyOutcome> {
    audit({ who: 'Simon Ford', act: 'Emailed HR: ready to onboard', obj: req.name, icon: 'ti-mail' });
    return later({ ok: true, sent: req.to.length, message: '' });
  }

  async offboard(upn: string): Promise<IOnboardOutcome> {
    audit({ who: 'Simon Ford', act: 'Closed personal folder', obj: upn, icon: 'ti-folder-off' });
    return later({
      ok: true,
      emailSent: false,
      message: 'Their access to their own folder has been removed. The folder and everything in it stays.',
    });
  }
}

class MockGroupService implements IGroupService {
  all(): Promise<IGroup[]> {
    return later(copy(store.groups));
  }
  /* Purpose only. Membership is Entra's, and a mock that let you edit it would
     teach the wrong thing about what this register does. */
  async update(id: string, patch: Partial<IGroup>): Promise<IGroup> {
    const index = store.groups.findIndex((g) => g.id === id);
    if (index < 0) throw new Error(`No group ${id}`);
    store.groups[index] = { ...store.groups[index], purpose: patch.purpose ?? store.groups[index].purpose };
    audit({ who: 'Simon Ford', act: 'Described group', obj: store.groups[index].name, icon: 'ti-users-group' });
    return later(copy(store.groups[index]));
  }
}

class MockLicenceService implements ILicenceService {
  skus(): Promise<ILicenceSku[]> {
    return later(copy(store.skus));
  }
  async update(id: string, patch: Partial<ILicenceSku>): Promise<ILicenceSku> {
    const index = store.skus.findIndex((k) => k.id === id);
    if (index < 0) throw new Error(`No licence ${id}`);
    store.skus[index] = { ...store.skus[index], ...copy(patch) };
    audit({ who: 'Simon Ford', act: 'Updated licence', obj: store.skus[index].name, icon: 'ti-license' });
    return later(copy(store.skus[index]));
  }
}

class MockNetworkService implements INetworkService {
  all(): Promise<INetworks> {
    return later(copy(store.networks));
  }
  /* One item per depot, so this is always an update — a depot either has a
     network record or has an empty one waiting to be filled in. */
  async save(depot: DepotId, patch: Partial<INetwork>): Promise<INetwork> {
    const current = store.networks[depot] ?? EMPTY_NETWORK;
    const updated = { ...current, ...copy(patch) };
    store.networks[depot] = updated;
    audit({ who: 'Simon Ford', act: 'Updated network', obj: depot, icon: 'ti-network' });
    return later(copy(updated));
  }
}

class MockLocationService implements ILocationService {
  all(): Promise<ILocations> {
    return later(copy(store.locations));
  }
  async create(name: string): Promise<ILocation> {
    const key = name.trim();
    store.locations[key] = { ...EMPTY_LOCATION };
    audit({ who: 'Simon Ford', act: 'Added site', obj: key, icon: 'ti-map-pin' });
    return later(copy(store.locations[key]));
  }
  /* One record per site, so this is always an update. */
  async save(depot: DepotId, patch: Partial<ILocation>): Promise<ILocation> {
    const current = store.locations[depot] ?? EMPTY_LOCATION;
    const updated = { ...current, ...copy(patch) };
    store.locations[depot] = updated;
    audit({ who: 'Simon Ford', act: 'Updated location', obj: depot, icon: 'ti-map-pin' });
    return later(copy(updated));
  }
}

class MockApplicationService implements IApplicationService {
  list(): Promise<IApplication[]> {
    return later(copy(store.applications));
  }
  async create(application: Omit<IApplication, 'id'>): Promise<IApplication> {
    const created: IApplication = { ...copy(application), id: nextId('a', store.applications) };
    store.applications.push(created);
    audit({ who: 'Simon Ford', act: 'Added application', obj: created.name, icon: 'ti-apps' });
    return later(copy(created));
  }
  async remove(id: string): Promise<void> {
    const app = store.applications.find((a) => a.id === id);
    store.applications = store.applications.filter((a) => a.id !== id);
    if (app) audit({ who: 'Simon Ford', act: 'Removed application', obj: app.name, icon: 'ti-trash' });
    await later(undefined);
  }
  async update(id: string, patch: Partial<IApplication>): Promise<IApplication> {
    const index = store.applications.findIndex((a) => a.id === id);
    if (index < 0) throw new Error(`No application ${id}`);
    store.applications[index] = { ...store.applications[index], ...copy(patch) };
    audit({ who: 'Simon Ford', act: 'Updated application', obj: store.applications[index].name, icon: 'ti-apps' });
    return later(copy(store.applications[index]));
  }
}

class MockSupplierService implements ISupplierService {
  list(): Promise<ISupplier[]> {
    return later(copy(store.suppliers));
  }
  async create(supplier: Omit<ISupplier, 'id'>): Promise<ISupplier> {
    const created: ISupplier = { ...copy(supplier), id: nextId('s', store.suppliers) };
    store.suppliers.push(created);
    audit({ who: 'Simon Ford', act: 'Added supplier', obj: created.name, icon: 'ti-building-store' });
    return later(copy(created));
  }
  async remove(id: string): Promise<void> {
    const supplier = store.suppliers.find((s) => s.id === id);
    store.suppliers = store.suppliers.filter((s) => s.id !== id);
    if (supplier) audit({ who: 'Simon Ford', act: 'Removed supplier', obj: supplier.name, icon: 'ti-trash' });
    await later(undefined);
  }
  async update(id: string, patch: Partial<ISupplier>): Promise<ISupplier> {
    const index = store.suppliers.findIndex((s) => s.id === id);
    if (index < 0) throw new Error(`No supplier ${id}`);
    store.suppliers[index] = { ...store.suppliers[index], ...copy(patch) };
    audit({ who: 'Simon Ford', act: 'Updated supplier', obj: store.suppliers[index].name, icon: 'ti-building-store' });
    return later(copy(store.suppliers[index]));
  }
}

class MockDomainService implements IDomainService {
  list(): Promise<IDomain[]> {
    return later(copy(store.domains));
  }
  async create(domain: Omit<IDomain, 'id'>): Promise<IDomain> {
    const created: IDomain = { ...copy(domain), id: nextId('dom', store.domains) };
    store.domains.push(created);
    audit({ who: 'Simon Ford', act: 'Added domain', obj: created.name, icon: 'ti-world' });
    return later(copy(created));
  }
  async update(id: string, patch: Partial<IDomain>): Promise<IDomain> {
    const index = store.domains.findIndex((d) => d.id === id);
    if (index < 0) throw new Error(`No domain ${id}`);
    store.domains[index] = { ...store.domains[index], ...copy(patch) };
    audit({ who: 'Simon Ford', act: 'Updated domain', obj: store.domains[index].name, icon: 'ti-world' });
    return later(copy(store.domains[index]));
  }
  async remove(id: string): Promise<void> {
    const domain = store.domains.find((d) => d.id === id);
    store.domains = store.domains.filter((d) => d.id !== id);
    if (domain) audit({ who: 'Simon Ford', act: 'Removed domain', obj: domain.name, icon: 'ti-trash' });
    await later(undefined);
  }
}

class MockBackupService implements IBackupService {
  jobs(): Promise<IBackup[]> {
    return later(copy(store.backups));
  }
  async saveJob(job: IBackup): Promise<IBackup> {
    const saved: IBackup = { ...copy(job), id: job.id || nextId('b', store.backups) };
    const index = store.backups.findIndex((b) => b.id === saved.id);
    if (index < 0) store.backups.push(saved);
    else store.backups[index] = saved;
    audit({
      who: 'Simon Ford',
      act: job.id ? 'Updated backup job' : 'Added backup job',
      obj: saved.name,
      icon: 'ti-shield-bolt',
    });
    return later(copy(saved));
  }
  async removeJob(id: string): Promise<void> {
    const job = store.backups.find((b) => b.id === id);
    store.backups = store.backups.filter((b) => b.id !== id);
    if (job) audit({ who: 'Simon Ford', act: 'Removed backup job', obj: job.name, icon: 'ti-trash' });
    await later(undefined);
  }
  plans(): Promise<IRecoveryPlan[]> {
    return later(copy(store.plans));
  }
  async savePlan(plan: IRecoveryPlan): Promise<IRecoveryPlan> {
    const saved: IRecoveryPlan = { ...copy(plan), id: plan.id || nextId('rp', store.plans) };
    const index = store.plans.findIndex((p) => p.id === saved.id);
    if (index < 0) store.plans.push(saved);
    else store.plans[index] = saved;
    audit({
      who: 'Simon Ford',
      act: plan.id ? 'Updated recovery plan' : 'Added recovery plan',
      obj: saved.name,
      icon: 'ti-route',
    });
    return later(copy(saved));
  }
  async removePlan(id: string): Promise<void> {
    const plan = store.plans.find((p) => p.id === id);
    store.plans = store.plans.filter((p) => p.id !== id);
    if (plan) audit({ who: 'Simon Ford', act: 'Removed recovery plan', obj: plan.name, icon: 'ti-trash' });
    await later(undefined);
  }
  tests(): Promise<IRestoreTest[]> {
    return later(copy(store.tests));
  }
  async logTest(test: IRestoreTest): Promise<void> {
    store.tests.unshift(copy(test));
    audit({ who: test.who, act: 'Logged restore test', obj: test.what, icon: 'ti-refresh-dot' });
    await later(undefined);
  }
}

class MockArticleService implements IArticleService {
  list(): Promise<IArticle[]> {
    return later(copy(store.articles));
  }
  get(id: string): Promise<IArticle | null> {
    return later(copy(store.articles.find((a) => a.id === id) ?? null));
  }
  async save(article: IArticle): Promise<IArticle> {
    const index = store.articles.findIndex((a) => a.id === article.id);
    const saved = { ...copy(article), upd: new Date().toISOString().slice(0, 10) };
    if (index < 0) store.articles.push(saved);
    else store.articles[index] = saved;
    audit({
      who: 'Simon Ford',
      act: index < 0 ? 'Created article' : 'Updated article',
      obj: saved.title,
      icon: index < 0 ? 'ti-file-plus' : 'ti-book-2',
    });
    return later(copy(saved));
  }
  async markReviewed(id: string): Promise<void> {
    const article = store.articles.find((a) => a.id === id);
    if (!article) throw new Error(`No article ${id}`);
    article.upd = new Date().toISOString().slice(0, 10);
    audit({ who: 'Simon Ford', act: 'Reviewed article', obj: article.title, icon: 'ti-checkbox' });
    await later(undefined);
  }
}

class MockProcedureService implements IProcedureService {
  templates(): Promise<IChecklistTemplate[]> {
    return later(copy(store.templates));
  }
  runs(): Promise<IChecklistRun[]> {
    return later(copy(store.runs));
  }
  async startRun(templateId: string, subject: string): Promise<IChecklistRun> {
    const run: IChecklistRun = {
      id: nextId('r', store.runs),
      tmpl: templateId,
      subject,
      started: new Date().toISOString().slice(0, 10),
      done: [],
      loc: 'Hull',
    };
    store.runs.push(run);
    audit({ who: 'Simon Ford', act: 'Started checklist', obj: subject, icon: 'ti-checklist' });
    return later(copy(run));
  }
  async toggleStep(runId: string, stepIndex: number, done: boolean): Promise<void> {
    const run = store.runs.find((r) => r.id === runId);
    if (!run) throw new Error(`No run ${runId}`);
    run.done = done ? [...new Set([...run.done, stepIndex])] : run.done.filter((i) => i !== stepIndex);
    await later(undefined);
  }
}

class MockCredentialService implements ICredentialService {
  list(): Promise<ICredential[]> {
    return later(copy(store.credentials));
  }
  async create(credential: Omit<ICredential, 'id'>): Promise<ICredential> {
    const created: ICredential = { ...copy(credential), id: nextId('c', store.credentials) };
    store.credentials.push(created);
    audit({ who: 'Simon Ford', act: 'Added credential', obj: created.name, icon: 'ti-key' });
    return later(copy(created));
  }
  async update(id: string, patch: Partial<ICredential>): Promise<ICredential> {
    const index = store.credentials.findIndex((c) => c.id === id);
    if (index < 0) throw new Error(`No credential ${id}`);
    store.credentials[index] = { ...store.credentials[index], ...copy(patch) };
    audit({ who: 'Simon Ford', act: 'Updated credential', obj: store.credentials[index].name, icon: 'ti-key' });
    return later(copy(store.credentials[index]));
  }
  async remove(id: string): Promise<void> {
    const cred = store.credentials.find((c) => c.id === id);
    store.credentials = store.credentials.filter((c) => c.id !== id);
    if (cred) audit({ who: 'Simon Ford', act: 'Removed credential', obj: cred.name, icon: 'ti-trash' });
    await later(undefined);
  }
  async markRotated(id: string): Promise<void> {
    const cred = store.credentials.find((c) => c.id === id);
    if (!cred) throw new Error(`No credential ${id}`);
    cred.rotated = new Date().toISOString().slice(0, 10);
    audit({ who: 'Simon Ford', act: 'Rotated credential', obj: cred.name, icon: 'ti-refresh' });
    await later(undefined);
  }
}

class MockAuditService implements IAuditService {
  list(take = 100): Promise<IAuditEntry[]> {
    return later(copy(store.audit.slice(0, take)));
  }
  async log(entry: Omit<IAuditEntry, 'when'>): Promise<void> {
    audit(entry);
    await later(undefined);
  }
}

class MockRecordTypeService implements IRecordTypeService {
  types(): Promise<IRecordType[]> {
    return later(copy(store.recordTypes.slice().sort((a, b) => a.sortOrder - b.sortOrder)));
  }
  async saveType(type: IRecordType): Promise<IRecordType> {
    const index = store.recordTypes.findIndex((t) => t.id === type.id);
    if (index < 0) store.recordTypes.push(copy(type));
    else store.recordTypes[index] = copy(type);
    audit({ who: 'Simon Ford', act: 'Saved record type', obj: type.title, icon: 'ti-layout-grid-add' });
    return later(copy(type));
  }
  async removeType(id: string): Promise<void> {
    store.recordTypes = store.recordTypes.filter((t) => t.id !== id);
    store.customRecords = store.customRecords.filter((r) => r.typeId !== id);
    await later(undefined);
  }
  records(typeId?: string): Promise<ICustomRecord[]> {
    const rows = typeId ? store.customRecords.filter((r) => r.typeId === typeId) : store.customRecords;
    return later(copy(rows));
  }
  /* Promotion happens on write, not on read — see lib/promote.ts for why, and
     for the rule itself, which the SharePoint provider shares. */
  async saveRecord(record: ICustomRecord): Promise<ICustomRecord> {
    const type = store.recordTypes.find((t) => t.id === record.typeId);
    const saved: ICustomRecord = {
      ...withPromotions(type, copy(record), seed.DEPOTS),
      id: record.id || nextId('cr', store.customRecords),
    };

    const index = store.customRecords.findIndex((r) => r.id === saved.id);
    if (index < 0) store.customRecords.push(saved);
    else store.customRecords[index] = saved;
    audit({ who: 'Simon Ford', act: 'Saved record', obj: saved.title, icon: 'ti-file-text' });
    return later(copy(saved));
  }
  async removeRecord(id: string): Promise<void> {
    store.customRecords = store.customRecords.filter((r) => r.id !== id);
    await later(undefined);
  }
}

class MockStatusService implements IStatusService {
  tiles(): Promise<IStatusTile[]> {
    return later(copy(store.tiles));
  }
}

class MockAttachmentService implements IAttachmentService {
  forRecord(recordId: string): Promise<IAttachment[]> {
    return later(copy(store.attachments[recordId] ?? []));
  }

  /* No bytes anywhere — the mock has no store to put them in. It records that a
     file was filed against a record, which is all any screen reads back. */
  async upload(recordId: string, _recordType: string, file: File): Promise<IAttachment> {
    const item: IAttachment = {
      name: file.name,
      kind: /\.(png|jpe?g|gif|webp|svg)$/i.test(file.name)
        ? 'img'
        : /\.pdf$/i.test(file.name)
          ? 'pdf'
          : 'other',
      size: `${Math.max(1, Math.round(file.size / 1024))} KB`,
      by: 'You',
      when: new Date().toISOString(),
    };
    (store.attachments[recordId] ??= []).push(item);
    await later(undefined);
    return item;
  }
}

class MockDepotService implements IDepotService {
  list(): IDepot[] {
    return seed.DEPOTS;
  }
  name(id: string): string {
    return seed.DEPOTS.find((d) => d.id === id)?.name ?? '—';
  }
}

class MockSettingsService implements ISettingsService {
  async all(): Promise<Record<string, string>> {
    return later({ ...store.settings });
  }
  async set(key: string, value: string): Promise<void> {
    store.settings[key] = value;
    await later(undefined);
  }
}

class MockAccessService implements IAccessService {
  me(): Promise<ICurrentUser> {
    return later({ name: 'Simon Ford', upn: 'simon.ford@hird.co.uk', role: 'admin', scope: [] });
  }
  roles(): Promise<IRoleDefinition[]> {
    return later(copy(store.roles));
  }
  modules(): Promise<IModule[]> {
    return later(copy(store.modules));
  }
  async setModuleEnabled(id: string, enabled: boolean): Promise<void> {
    const module = store.modules.find((m) => m.id === id);
    if (module) module.enabled = enabled;
    await later(undefined);
  }
}

class MockSyncService implements ISyncService {
  lastRun(): Promise<{ at: string; ok: boolean; summary: string } | null> {
    return later({
      at: '2026-07-29 02:00',
      ok: true,
      summary: '64 accounts, 10 SKUs, 31 managed devices. 2 differences flagged for review.',
    });
  }
  /* Simulated. The real sync is a timer-triggered Function running app-only —
     nothing about it happens in the browser (§10). */
  async trigger(): Promise<void> {
    audit({ who: 'Simon Ford', act: 'Triggered sync', obj: 'Microsoft 365', icon: 'ti-cloud-download' });
    await new Promise((resolve) => setTimeout(resolve, 1400));
  }
}

/* The Onboarding app, in the mock bundle: three templates and an empty case list, so the Add a
   joiner flow can be walked end to end with nothing configured. */
class MockJoinerService implements IJoinerService {
  private cases_: IJoinerCase[] = [];

  async templates(): Promise<IRoleTemplate[]> {
    const list: IRoleTemplate[] = [
      {
        id: 'tpl-crane', title: 'Crane operator', jobTitle: 'Crane operator', depot: 'Hull',
        department: 'Hull Machine Moving',
        apps: { customerservice: 'user' }, kit: ['Mobile phone'], trainingPack: 'Crane operator', inductionPack: 'Standard plus lifting',
      },
      {
        id: 'tpl-office', title: 'Office admin', jobTitle: 'Office administrator', depot: 'Doncaster',
        department: 'Doncaster Office', apps: {}, kit: ['Laptop'],
      },
      {
        id: 'tpl-hire', title: 'Hire desk', jobTitle: 'Hire controller', depot: 'London',
        department: 'London Office', apps: { customerservice: 'user' }, kit: ['Laptop', 'Mobile phone'],
      },
    ];
    return later(list);
  }

  async cases(): Promise<IJoinerCase[]> {
    return later([...this.cases_]);
  }

  async openCase(input: IOpenCase): Promise<IOpenedCase> {
    const existing = this.cases_.find((c) => c.upn.toLowerCase() === input.upn.toLowerCase());
    if (existing) return later({ ...existing, created: false });
    const made: IJoinerCase = {
      id: `case-${this.cases_.length + 1}`,
      name: input.name, upn: input.upn, graphId: input.graphId, templateId: input.templateId,
      jobTitle: input.jobTitle, depot: input.depot, department: input.department, start: input.start,
      stage: 'HR', handedToHrOn: new Date().toISOString(),
    };
    this.cases_.push(made);
    return later({ ...made, created: true });
  }

  async hrRecipients(): Promise<string[]> {
    return later(['richard.christiansen@hird.co.uk']);
  }
}

export function createMockServices(): ILedgerServices {
  return {
    people: new MockPeopleService(),
  groups: new MockGroupService(),
    devices: new MockDeviceService(),
    directory: new MockDirectoryService(),
    trainingScope: new MockTrainingScopeService(),
    onboarding: new MockOnboardingService(),
    leave: new MockLeaveSyncService(),
    windDown: new MockWindDownService(),
    licences: new MockLicenceService(),
    networks: new MockNetworkService(),
    locations: new MockLocationService(),
    applications: new MockApplicationService(),
    suppliers: new MockSupplierService(),
    domains: new MockDomainService(),
    backups: new MockBackupService(),
    articles: new MockArticleService(),
    procedures: new MockProcedureService(),
    credentials: new MockCredentialService(),
    audit: new MockAuditService(),
    recordTypes: new MockRecordTypeService(),
    status: new MockStatusService(),
    attachments: new MockAttachmentService(),
    depots: new MockDepotService(),
    settings: new MockSettingsService(),
    access: new MockAccessService(),
    sync: new MockSyncService(),
    joiners: new MockJoinerService(),
  };
}
